import { Vector2 } from 'three';

/**
 * Vertical-plane opening profiles.
 *
 * A panel opening is a closed 2D outline in the panel's own front plane (width x height),
 * extruded horizontally through the panel by `CutoutInfo.verticalProfile`. These builders
 * produce those outlines. They are shape primitives only — nothing here knows about vents,
 * SKUs or panels, so any future opening (louvre, grille, speaker port) can reuse them.
 *
 * Origin is the centre of the opening, +y up, matching `createCutoutMeshCfn`.
 */

/** Points per 90 degrees of arc. Eight reads as a smooth cap at panel scale without inflating the CSG. */
const ARC_SEGMENTS_PER_QUARTER = 8;

export type SlotSpec = {
	/** Centre of the slot, relative to the opening centre. +y is up. */
	cx: number;
	cy: number;
	width: number;
	height: number;
	/** Corner radius. Clamped to half the shorter side; `height / 2` yields a stadium. */
	radius?: number;
};

/**
 * Rounded rectangle as a closed point loop, wound counter-clockwise.
 * `radius = 0` degenerates to a plain rectangle, `radius = height / 2` to a stadium.
 */
export const roundedSlotProfile = (spec: SlotSpec): Array<Vector2> => {
	const { cx, cy, width, height } = spec;
	const halfW = width / 2;
	const halfH = height / 2;
	const radius = Math.max(0, Math.min(spec.radius ?? 0, halfW, halfH));

	if (radius <= 0) {
		return [
			new Vector2(cx - halfW, cy - halfH),
			new Vector2(cx + halfW, cy - halfH),
			new Vector2(cx + halfW, cy + halfH),
			new Vector2(cx - halfW, cy + halfH)
		];
	}

	const points: Array<Vector2> = [];
	const straightW = halfW - radius;
	const straightH = halfH - radius;
	// corner centres, counter-clockwise from bottom-right
	const corners: Array<[number, number, number]> = [
		[cx + straightW, cy - straightH, -Math.PI / 2],
		[cx + straightW, cy + straightH, 0],
		[cx - straightW, cy + straightH, Math.PI / 2],
		[cx - straightW, cy - straightH, Math.PI]
	];

	for (const [ccx, ccy, startAngle] of corners) {
		for (let i = 0; i <= ARC_SEGMENTS_PER_QUARTER; i++) {
			const angle = startAngle + ((Math.PI / 2) * (i / ARC_SEGMENTS_PER_QUARTER));
			points.push(new Vector2(ccx + (Math.cos(angle) * radius), ccy + (Math.sin(angle) * radius)));
		}
	}

	return points;
};

/**
 * Converts SVG-space slot coordinates into centred, y-up profile points.
 *
 * The component SVGs lay slots out from the opening's top-left with y increasing downward
 * (see `mok-vents-back.svg.tsx`). This is the single conversion between that space and the
 * 3D one, so the 2D artwork stays the source of truth for slot positions.
 */
export const slotFromSvgRect = (
	svgX: number, svgY: number, slotWidth: number, slotHeight: number,
	openingWidth: number, openingHeight: number, radius?: number
): Array<Vector2> => {
	return roundedSlotProfile({
		cx: (svgX + (slotWidth / 2)) - (openingWidth / 2),
		cy: (openingHeight / 2) - (svgY + (slotHeight / 2)),
		width: slotWidth,
		height: slotHeight,
		radius
	});
};

/** Closed polygon from raw SVG-space points, centred and flipped to y-up. */
export const polygonFromSvgPoints = (
	svgPoints: Array<[number, number]>, openingWidth: number, openingHeight: number
): Array<Vector2> => {
	return svgPoints.map(([x, y]) => new Vector2(x - (openingWidth / 2), (openingHeight / 2) - y));
};
